import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores';
import { syncEngine } from './lib/sync';
import { onAuthError } from './lib/salesforce';
import { vraagDuurzameOpslag } from './lib/media/queue';
import { db } from './lib/db/database';
import { stelWakeLockIn, startWakeLockHerstel } from './lib/media/wakelock';
import { useLiveQuery } from 'dexie-react-hooks';

// Layouts
import { AppLayout } from './components/layout/AppLayout';
import { AuthLayout } from './components/layout/AuthLayout';

// Pages
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { RoutesPage } from './pages/RoutesPage';
import { RouteDetailPage } from './pages/RouteDetailPage';
import { WorkOrderPage } from './pages/WorkOrderPage';
import { InspectionPage } from './pages/InspectionPage';
import { SettingsPage } from './pages/SettingsPage';
import { AddItemPage } from './pages/AddItemPage';
import { RoutePreparationPage } from './pages/RoutePreparationPage';
import { PrepareRouteDetailPage } from './pages/PrepareRouteDetailPage';
import { BusLoadingPage } from './pages/BusLoadingPage';
import { LoadBusDetailPage } from './pages/LoadBusDetailPage';
import { UnderlayPage } from './pages/UnderlayPage';
import { VoorinspectieListPage } from './pages/VoorinspectieListPage';
import { VoorinspectieDetailPage } from './pages/VoorinspectieDetailPage';
import { ReinigenListPage } from './pages/ReinigenListPage';
import { ReinigenDetailPage } from './pages/ReinigenDetailPage';
import { FotoTijdensListPage } from './pages/FotoTijdensListPage';
import { NaInspectieListPage } from './pages/NaInspectieListPage';
import { NaInspectieDetailPage } from './pages/NaInspectieDetailPage';
import { SelfServiceListPage } from './pages/SelfServiceListPage';
import { SelfServiceDetailPage } from './pages/SelfServiceDetailPage';
import { BerichtenPage } from './pages/BerichtenPage';
import { InpakkenListPage } from './pages/InpakkenListPage';
import { InpakkenDetailPage } from './pages/InpakkenDetailPage';
import { ZoekPage } from './pages/ZoekPage';
import { SocialPage } from './pages/SocialPage';
import { CameraStationPage } from './pages/CameraStationPage';

// Components
import { OfflineIndicator } from './components/ui/OfflineIndicator';

function App() {
  const { isAuthenticated } = useAuthStore();

  // De wachtrij met captures is de enige kopie van een foto, dus vraag de
  // browser die opslag als duurzaam te merken. Zonder deze vlag mag hij de
  // hele origin weggooien onder schijfdruk (en evict iOS Safari best-effort
  // origins na ongeveer een week zonder gebruik). Eén keer per app-load, stil
  // bij weigering: een geweigerde aanvraag mag niets blokkeren.
  useEffect(() => {
    void vraagDuurzameOpslag();
  }, []);

  // Zolang er media op het toestel wacht: scherm aan houden. Uploaden gebeurt
  // in dit tabblad en een telefoon met scherm uit bevriest het, waardoor een
  // wachtrij dagen kan blijven staan. Alleen bij een niet-lege wachtrij, want
  // een lock die altijd aan staat kost de accu.
  const wachtrijDiepte = useLiveQuery(() => db.mediaQueue.count(), [], 0) ?? 0;

  useEffect(() => startWakeLockHerstel(), []);

  useEffect(() => {
    stelWakeLockIn(wachtrijDiepte > 0);
  }, [wachtrijDiepte]);

  // Start sync engine when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      syncEngine.startAutoSync(30000);
      return () => { syncEngine.stopAutoSync(); };
    }
  }, [isAuthenticated]);

  // Listen for auth errors (401) and auto-logout
  useEffect(() => {
    return onAuthError(() => {
      useAuthStore.getState().logout();
    });
  }, []);

  // De sessie leeft zolang de app in gebruik is. Chrome op Android gooit een
  // tabblad op de achtergrond weg; komt de chauffeur terug, dan hydrateert de
  // store uit localStorage en pakken we hier de klok weer op. Ligt de app langer
  // stil dan het inactiviteitsvenster, dan vervalt de sessie hier alsnog.
  useEffect(() => {
    const bijTerugkeer = () => {
      if (document.visibilityState !== 'visible') return;
      if (useAuthStore.getState().controleerSessieVerval()) return;
      useAuthStore.getState().raakSessieAan();
    };

    bijTerugkeer();
    document.addEventListener('visibilitychange', bijTerugkeer);
    window.addEventListener('focus', bijTerugkeer);
    return () => {
      document.removeEventListener('visibilitychange', bijTerugkeer);
      window.removeEventListener('focus', bijTerugkeer);
    };
  }, []);

  return (
    <>
      <OfflineIndicator />
      <Routes>
        {/* Public routes */}
        <Route element={<AuthLayout />}>
          <Route
            path="/login"
            element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />}
          />
        </Route>

        {/* Protected routes */}
        <Route
          element={isAuthenticated ? <AppLayout /> : <Navigate to="/login" replace />}
        >
          <Route path="/" element={<DashboardPage />} />
          <Route path="/routes" element={<RoutesPage />} />
          <Route path="/routes/:routeId" element={<RouteDetailPage />} />
          <Route path="/preparation" element={<RoutePreparationPage />} />
          <Route path="/preparation/:routeId" element={<PrepareRouteDetailPage />} />
          <Route path="/bus-loading" element={<BusLoadingPage />} />
          <Route path="/bus-loading/:routeId" element={<LoadBusDetailPage />} />
          <Route path="/underlay" element={<UnderlayPage />} />
          <Route path="/voorinspectie" element={<VoorinspectieListPage />} />
          <Route path="/voorinspectie/:itemId" element={<VoorinspectieDetailPage />} />
          <Route path="/reinigen" element={<ReinigenListPage />} />
          <Route path="/reinigen/:itemId" element={<ReinigenDetailPage />} />
          <Route path="/foto-tijdens" element={<FotoTijdensListPage />} />
          <Route path="/na-inspectie" element={<NaInspectieListPage />} />
          <Route path="/na-inspectie/:itemId" element={<NaInspectieDetailPage />} />
          <Route path="/self-service" element={<SelfServiceListPage />} />
          <Route path="/self-service/:workOrderId" element={<SelfServiceDetailPage />} />
          <Route path="/inpakken" element={<InpakkenListPage />} />
          <Route path="/inpakken/:orderId" element={<InpakkenDetailPage />} />
          <Route path="/zoek" element={<ZoekPage />} />
          <Route path="/social" element={<SocialPage />} />
          <Route path="/camera-station" element={<CameraStationPage />} />
          <Route path="/workorder/:workOrderId" element={<WorkOrderPage />} />
          <Route path="/workorder/:workOrderId/inspection" element={<InspectionPage />} />
          <Route path="/workorder/:workOrderId/inspection/:itemId" element={<InspectionPage />} />
          <Route path="/workorder/:workOrderId/add-item" element={<AddItemPage />} />
          <Route path="/berichten" element={<BerichtenPage />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Route>

        {/* Catch all */}
        <Route path="*" element={<Navigate to={isAuthenticated ? '/' : '/login'} replace />} />
      </Routes>
    </>
  );
}

export default App;
